import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FaTrash, FaBars, FaSave } from "react-icons/fa";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

import { ReorderListModal } from "../../../components/ReorderListModal";
import { AddCourseModal } from "../../../components/AddCourseModal";
import MultipleSelectInput from "@/components/MultipleSelectInput";

import FileInput from "@/components/FileInput";
import api from "@/services/api";

export default function Edit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [careerPath, setCareerPath] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({ name: "", description: "", image: "", tags: [] });

  // Available tags for career paths
  useEffect(() => {
    getCareerPath();
  }, [id]);

  async function getCareerPath() {
    try {
      setIsLoading(true);
      const { data, ok } = await api.get(`/career_path/${id}`);
      if (!ok) throw new Error("Failed to fetch career path");
      setCareerPath(data);
      setFormData({
        name: data.name || "",
        description: data.description || "",
        image: data.image || "",
        tags: data.tags || [],
      });
    } catch (error) {
      toast.error("Failed to fetch career path details");
    } finally {
      setIsLoading(false);
    }
  }

  function handleInputChange(field, value) {
    setFormData({ ...formData, [field]: value });
  }

  async function handleSave() {
    try {
      setIsSaving(true);
      const { ok, data } = await api.put(`/career_path/${careerPath._id}`, formData);
      if (!ok) throw new Error();
      setCareerPath({ ...careerPath, ...data });
      toast.success("Career path updated");
    } catch (error) {
      toast.error("Failed to update career path");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAddTraining(training) {
    try {
      setIsLoading(true);
      const alreadyExists = careerPath.trainings.some((t) => t.training_id === training._id);
      if (alreadyExists) {
        toast.error("Training already in career path");
        setIsLoading(false);
        return;
      }
      const newTrainings = [
        ...careerPath.trainings,
        {
          training_id: training._id,
          training_title: training.title,
          training_description: training.description,
          training_image: training.image,
          training_category: training.category,
          training_position: careerPath.trainings.length,
        },
      ];
      const { ok, data } = await api.put(`/career_path/${careerPath._id}`, { ...careerPath, trainings: newTrainings });
      if (!ok) throw new Error();
      setCareerPath(data);
      toast.success("Course added to career path");
    } catch (error) {
      toast.error("Failed to add course");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleRemoveCourse(course) {
    if (!window.confirm(`Remove course '${course.training_title}' from this career path?`)) return;
    try {
      setIsLoading(true);
      const newTrainings = careerPath.trainings.filter((t) => t.training_id !== course.training_id);
      const { ok, data } = await api.put(`/career_path/${careerPath._id}`, { ...careerPath, trainings: newTrainings });
      if (!ok) throw new Error();
      setCareerPath(data);
      toast.success("Course removed from career path");
    } catch (error) {
      toast.error("Failed to remove course");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleReorderCourses(newOrder) {
    try {
      setIsLoading(true);
      const newTrainings = newOrder.map((course, idx) => {
        const t = careerPath.trainings.find((t) => t.training_id === course.training_id);
        return { ...t, training_position: idx };
      });
      const { ok, data } = await api.put(`/career_path/${careerPath._id}`, { ...careerPath, trainings: newTrainings });
      if (!ok) throw new Error();
      setCareerPath(data);
      toast.success("Courses reordered");
    } catch (error) {
      toast.error("Failed to reorder courses");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDeleteCareerPath() {
    if (!window.confirm("Are you sure you want to delete this career path?")) return;
    try {
      setIsLoading(true);
      const { ok } = await api.remove(`/career_path/${careerPath._id}`);
      if (!ok) throw new Error();
      toast.success("Career path deleted successfully!");
      navigate("/selecole/career-path");
    } catch (error) {
      toast.error("Failed to delete career path");
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading || !careerPath) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  const courses = (careerPath.trainings || []).slice().sort((a, b) => (a.training_position || 0) - (b.training_position || 0));

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="md:w-1/3 w-full">
          <div className="rounded-lg border border-gray-200 p-6 space-y-4 bg-gray-50">
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white"
                  placeholder="Enter career path name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white"
                  rows="5"
                  placeholder="Enter description"
                />
              </div>
              <div>
                <MultipleSelectInput
                  value={formData.tags}
                  onChange={(tags) => handleInputChange("tags", tags)}
                  label="Tags"
                  placeholder="Add tags..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                <FileInput value={formData.image} folder="/career_path" onChange={(e) => handleInputChange("image", e.target.value[0])} name="careerPathImage" />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={handleDeleteCareerPath}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50">
                  <FaTrash className="w-4 h-4" />
                  Delete
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50">
                  <FaSave className="w-4 h-4" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="md:w-2/3 w-full">
          <div className="rounded-lg border border-gray-200 p-6 space-y-4 bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <p className="font-semibold text-lg text-gray-700">Courses in this Career Path</p>
              <div className="flex gap-2">
                <ReorderListModal
                  items={courses.map((course) => ({
                    ...course,
                    name: course.training_title,
                    description: course.training_description,
                  }))}
                  onReorder={handleReorderCourses}>
                  <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                    <FaBars className="w-3 h-3" />
                    <span>Reorder Courses</span>
                  </button>
                </ReorderListModal>
                <AddCourseModal onAdd={handleAddTraining} existingIds={courses.map((c) => c.training_id)} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {courses.length === 0 && <div className="text-gray-500 text-center col-span-2">No courses in this career path.</div>}
              {courses.map((course) => (
                <div key={course.training_id} className="flex items-center justify-between border-b border-gray-200 py-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      {course.training_image ? (
                        <img src={course.training_image} alt={course.training_title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <span className="text-gray-400 text-xs">No image</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{course.training_title}</h4>
                      <p className="text-sm text-gray-500 line-clamp-1">{course.training_description}</p>
                      <span className="inline-block px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-full mt-1">{course.training_category || "TECH"}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 text-blue-600 hover:text-blue-800 transition-colors"
                      title="Edit Course"
                      onClick={() => navigate(`/selecole/course/${course.training_id}/edit`)}>
                      <FiEdit2 className="w-4 h-4" />
                    </button>
                    <button
                      className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 text-red-600 hover:text-red-800 transition-colors"
                      title="Remove Course"
                      onClick={() => handleRemoveCourse(course)}>
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
